import { shallowRef, provide, inject, onMounted, onBeforeUnmount } from 'vue-demi';

/**
 * Injection symbol for the context menu composable.
 * @type {symbol}
 */
const useContextMenuSymbol = Symbol('useContextMenu');

/**
 * Provides context menu state and control functions to descendant components.
 *
 * Sets up reactive references for the active context menu element and its position.
 * Adds global listeners for closing the context menu when clicking outside or on scroll.
 * Should be called once at a high level in the component tree.
 */
export function provideContextMenu() {
  /**
   * The currently active context menu DOM element.
   * @type {import('vue-demi').ShallowRef<HTMLElement | null>}
   */
  const activeContextMenuEl = shallowRef(null);

  /**
   * The screen coordinates where the context menu is displayed.
   * @type {import('vue-demi').ShallowRef<{ x: number, y: number }>}
   */
  const activeContextMenuPosition = shallowRef({ x: 0, y: 0 });

  /**
   * Whether the context menu is locked and should not close on click outside.
   * @type {import('vue-demi').ShallowRef<boolean>}
   */
  const isContextMenuLocked = shallowRef(false);

  /**
   * Opens the context menu and sets the active element and position.
   * @param {HTMLElement} el - The element to set as the active context menu.
   * @param {{ x: number, y: number }} [position={ x: 0, y: 0 }] - The screen coordinates for positioning the menu.
   */
  function openContextMenu(el, position = { x: 0, y: 0 }) {
    activeContextMenuEl.value = el;
    activeContextMenuPosition.value = position;
  }

  /**
   * Closes the currently active context menu.
   */
  function closeContextMenu() {
    activeContextMenuEl.value = null;
    activeContextMenuPosition.value = { x: 0, y: 0 };
  }

  /**
   * Locks the context menu to prevent it from closing on click outside.
   */
  function lockContextMenu() {
    isContextMenuLocked.value = true;
  }

  /**
   * Unlocks the context menu to allow it to close on click outside.
   */
  function unlockContextMenu() {
    isContextMenuLocked.value = false;
  }

  /**
   * Closes the context menu if the click is outside the active element.
   * @param {MouseEvent} e - The click event.
   */
  function closeOnClickOutside(e) {
    if (isContextMenuLocked.value) {
      return;
    }
    if (!activeContextMenuEl.value?.contains(e.target) && !e.target.closest('[data-context-menu]')) {
      closeContextMenu();
    }
  }

  onMounted(() => {
    document.addEventListener('contextmenu', closeOnClickOutside, false);
    document.addEventListener('click', closeOnClickOutside, true);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('contextmenu', closeOnClickOutside, false);
    document.removeEventListener('click', closeOnClickOutside, true);
  });

  provide(useContextMenuSymbol, {
    openContextMenu,
    closeContextMenu,
    closeOnClickOutside,
    lockContextMenu,
    unlockContextMenu,
    activeContextMenuEl,
    activeContextMenuPosition,
  });
}

/**
 * Injects the context menu composable.
 *
 * Allows access to `openContextMenu`, `closeContextMenu`, `lockContextMenu`, `unlockContextMenu`,
 * and reactive refs from `provideContextMenu`.
 *
 * @returns {{
 *   openContextMenu: (el: HTMLElement, position?: { x: number, y: number }) => void,
 *   closeContextMenu: () => void,
 *   lockContextMenu: () => void,
 *   unlockContextMenu: () => void,
 *   activeContextMenuEl: import('vue-demi').ShallowRef<HTMLElement | null>,
 *   activeContextMenuPosition: import('vue-demi').ShallowRef<{ x: number, y: number }>
 * } | undefined} - The context menu API, or undefined if not provided.
 */
export function useContextMenu() {
  return inject(useContextMenuSymbol);
}
